import { Injectable } from '@angular/core';

/**
 * Service for centralized sessionStorage access
 *
 * sessionStorage is tab-scoped (cleared when tab closes) but NOT user-scoped.
 * This creates contamination risk when users share tabs:
 * - User A logs in → sessionStorage populated
 * - User A logs out → sessionStorage NOT automatically cleared
 * - User B logs in to same tab → inherits User A's sessionStorage!
 *
 * This service provides:
 * - Consistency: All storage through services (no direct sessionStorage access)
 * - Testability: Can mock sessionStorage in tests
 * - Safety: Explicit clearAll() on logout prevents data leakage
 *
 * Use sessionStorage for:
 * - Ephemeral UI state within a tab session
 * - Data that should NOT persist across browser restarts
 *
 * NOTE: Auth tokens are stored in localStorage (not sessionStorage) to survive
 * browser tab discarding. See AuthService for token management.
 *
 * Do NOT use sessionStorage for:
 * - User preferences (use UserStorageService with user-scoping)
 * - Data that persists across sessions (use UserStorageService)
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  /**
   * Get item from sessionStorage
   * Returns null if key doesn't exist
   */
  getItem(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  /**
   * Set item in sessionStorage
   * Data is tab-scoped and cleared when tab closes
   */
  setItem(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  /**
   * Remove specific item from sessionStorage
   */
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Clear ALL sessionStorage for current tab
   *
   * CRITICAL: Call this on logout to prevent session data leakage
   * when multiple users share the same tab (common in testing scenarios)
   */
  clearAll(): void {
    sessionStorage.clear();
  }

  /**
   * Check if a key exists in sessionStorage
   */
  hasItem(key: string): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Get all keys currently in sessionStorage
   * Useful for debugging
   */
  getAllKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys;
  }
}
